// Pure feature-chat translation: wire → view model, and the ask body. No Vue, no store, no http.
import type {
  FeatureAskRequestDto,
  FeatureAskResponseDto,
  FeatureChatHistoryResponseDto,
  FeatureChatMessageDto,
} from '../models/featureChat.dto'
import type {
  FeatureChatMessage,
  FeatureChatMode,
  FeatureChatRole,
  FeatureChatTargetRef,
  FeatureChatTurnError,
} from '../models/featureChat.types'

import { toMessageCredits } from './messageMapper'
import { extractSetupParts, hasSetupParts } from './orpheusSetupParts'

/** v3 capped these at render time (`featureChat.js`); the cap belongs to the mapper, not a template. */
const MAX_CITATIONS = 12
const MAX_FOLLOWUPS = 3

function strings(value: unknown, limit: number): string[] {
  if (!Array.isArray(value)) return []
  return value
    .filter((item): item is string => typeof item === 'string' && item !== '')
    .slice(0, limit)
}

/**
 * The ask body.
 *
 * `target_ref` is omitted unless it is a non-empty plain object: the backend field is a
 * `DictField`, so a scalar or a list is a 400 by design, and sending `{}` would ask about nothing.
 * `focus_ref` and `session_id` are omitted rather than sent empty — the serializer defaults them.
 */
export function buildFeatureChatAskBody(input: {
  featureKey: string
  question: string
  targetRef?: FeatureChatTargetRef | null | undefined
  focusRef?: string | null | undefined
  sessionId?: string | null | undefined
  mode?: FeatureChatMode | undefined
  /** "Start over": never sent alongside a `session_id` — that is a 400. */
  newSession?: boolean | undefined
}): FeatureAskRequestDto {
  const body: FeatureAskRequestDto = {
    feature_key: input.featureKey,
    question: input.question.trim(),
  }

  const targetRef = input.targetRef
  if (
    targetRef &&
    typeof targetRef === 'object' &&
    !Array.isArray(targetRef) &&
    Object.keys(targetRef).length > 0
  ) {
    body.target_ref = targetRef
  }

  const focusRef = input.focusRef?.trim()
  if (focusRef) body.focus_ref = focusRef
  if (input.mode === 'setup') body.mode = 'setup'
  if (input.sessionId) body.session_id = input.sessionId
  else if (input.newSession) body.new_session = true

  return body
}

/** A `target_ref` built from one id column, or null when the record carries no usable id. */
export function featureChatTargetRef(
  key: string,
  value: number | string | null | undefined,
): FeatureChatTargetRef | null {
  if (value === null || value === undefined || value === '') return null
  return { [key]: value }
}

/**
 * Turns are stamped LOCALLY at creation, as old did (`FeatureOrpheusPanel.vue:createMessage`), so
 * every bubble carries a timestamp the moment it appears. The server's `created_at` is preferred
 * when it sends one, but its absence must not leave the turn undated.
 */
function nowIso(): string {
  return new Date().toISOString()
}

function emptyMessage(role: FeatureChatRole, localId: string): FeatureChatMessage {
  return {
    localId,
    role,
    content: '',
    createdAt: nowIso(),
    pending: false,
    citations: [],
    followups: [],
    needsMoreContext: false,
    error: null,
  }
}

/** The user's turn, appended before the request so the panel echoes it immediately. */
export function userMessage(localId: string, content: string): FeatureChatMessage {
  return { ...emptyMessage('user', localId), content }
}

/** The assistant placeholder that the answer (or the failure) later replaces in place. */
export function pendingAssistantMessage(localId: string): FeatureChatMessage {
  return { ...emptyMessage('assistant', localId), pending: true }
}

/**
 * The answered assistant turn.
 *
 * A 200 whose envelope says `success: false` is a failure the transport never raised, so it maps
 * to the same failed turn a thrown error would: an answer nobody wrote must not render as one.
 */
export function answeredAssistantMessage(
  localId: string,
  dto: FeatureAskResponseDto,
): FeatureChatMessage {
  const answer = typeof dto.answer === 'string' ? dto.answer : ''
  // A setup question can arrive with no text of its own: its card is the answer then.
  const parts = extractSetupParts(dto)
  if (dto.success === false || (answer === '' && !hasSetupParts(parts))) {
    return failedAssistantMessage(localId, {
      kind: 'generic',
      retryAfterSeconds: null,
      message: null,
    })
  }
  return {
    ...emptyMessage('assistant', localId),
    content: answer,
    createdAt: dto.message?.created_at ?? nowIso(),
    citations: strings(dto.citations, MAX_CITATIONS),
    followups: strings(dto.followup_questions, MAX_FOLLOWUPS),
    needsMoreContext: dto.needs_more_context === true,
    setupStep: parts.setup,
    setupCard: parts.card,
    filePreview: parts.filePreview,
    credits: toMessageCredits(dto.credits),
  }
}

export function failedAssistantMessage(
  localId: string,
  error: FeatureChatTurnError,
): FeatureChatMessage {
  return { ...emptyMessage('assistant', localId), error }
}

/** Persisted rows → view messages. `system` rows are internal and never rendered. */
export function mapHistory(
  dto: Pick<FeatureChatHistoryResponseDto, 'messages'>,
  localId: (row: FeatureChatMessageDto) => string,
): FeatureChatMessage[] {
  const rows = Array.isArray(dto.messages) ? dto.messages : []
  return rows
    .filter((row) => row.author_type === 'user' || row.author_type === 'assistant')
    .map((row) => {
      const message: FeatureChatMessage = {
        ...emptyMessage(row.author_type as FeatureChatRole, localId(row)),
        content: typeof row.content_text === 'string' ? row.content_text : '',
        createdAt: row.created_at ?? null,
      }
      if (row.author_type !== 'assistant') return message
      // A setup session's stored answer keeps its parts and credits in `payload`, with the
      // draft status the history stamped on its card.
      const parts = extractSetupParts(row.payload ?? {})
      return {
        ...message,
        setupStep: parts.setup,
        setupCard: parts.card,
        filePreview: parts.filePreview,
        credits: toMessageCredits(row.payload?.credits),
      }
    })
}
